import { useEffect, useMemo, useRef, useState } from 'react';
import apiService, { ServiceType } from '../services/api';
import { bareSymbol } from './useLivePrices';
import type { PnlInterval } from './usePnlHistory';
import {
    buildNotionalSeries,
    clockOffsetSeconds,
    previousCloseMarks,
    vnDay,
    type Candle,
    type NotionalHistoryPoint,
    type PreviousCloseMarks,
} from '../utils/pairSpreadHistory';

/**
 * Fetch the recent sessions' candles for every leg, once, and hand them to
 * `utils/pairSpreadHistory` — which owns the arithmetic, including why candle
 * times are shifted back 25200 s here.
 *
 * Re-runs when the SET of legs changes or the operator picks a different
 * interval — editing an unrelated field on the pair form costs nothing.
 */

const coreService = apiService.getService(ServiceType.CORE);

const CANDLE_LIMIT = 500;

/**
 * Candle scale per pane interval — the operator's own detail-vs-depth choice.
 *
 *   1min → ~8 trading hours  ≈ 1 session (plus the previous close)
 *   5min → ~41 trading hours ≈ 6 sessions
 *   1h   → ~500 hours        ≈ 60 sessions
 *
 * `5s` maps to `1min` because the endpoint has no sub-minute scale. The live tail
 * is sampled from the websocket every 5 s regardless.
 */
const CANDLE_SCALE: Record<PnlInterval, string> = {
    '5s': '1min',
    '1min': '1min',
    '5min': '5min',
    '1h': '1h',
};

const EMPTY_MARKS: PreviousCloseMarks = { future: null, byStock: {} };

export type { NotionalHistoryPoint, PreviousCloseMarks } from '../utils/pairSpreadHistory';

export interface PairSpreadHistoryInput {
    futureSymbol?: string | null;
    stocks: { symbol: string; proportion?: number | null }[];
    /** Contract multiplier (VN30F = 100000). */
    multiplier: number;
    /** The pane's interval — picks the candle scale, and with it how far back. */
    interval: PnlInterval;
    enabled?: boolean;
}

export interface PairSpreadHistory {
    /** Both legs as notional, raw VND, one contract. */
    notional: NotionalHistoryPoint[];
    /** Each leg's close before TODAY — what the live spread is measured from. */
    marks: PreviousCloseMarks;
    loading: boolean;
    /** Set when the futures leg could not be loaded; the live part still works. */
    error: string | null;
}

async function fetchCandles(symbol: string, scale: string): Promise<Candle[]> {
    const response = await coreService.get('/price/chart', {
        params: { tickerSymbol: bareSymbol(symbol), timeScale: scale, limit: CANDLE_LIMIT },
        timeout: 15000,
    });
    const rows = response.data?.ohlc;
    if (!Array.isArray(rows)) return [];
    const offset = clockOffsetSeconds(symbol);
    return rows
        .map((c: any) => ({
            time: Math.floor(new Date(c.time).getTime() / 1000) - offset,
            open: Number(c.open),
            close: Number(c.close),
        }))
        .filter((c: Candle) => Number.isFinite(c.time) && c.close > 0)
        .sort((a: Candle, b: Candle) => a.time - b.time);
}

export function usePairSpreadHistory({
    futureSymbol,
    stocks,
    multiplier,
    interval,
    enabled = true,
}: PairSpreadHistoryInput): PairSpreadHistory {
    const [state, setState] = useState<PairSpreadHistory>({
        notional: [], marks: EMPTY_MARKS, loading: false, error: null,
    });

    // Refetch on the SET of legs, not on every render of the array.
    const key = useMemo(
        () =>
            [
                futureSymbol ?? '',
                ...stocks.map((s) => `${s.symbol}:${s.proportion ?? ''}`).sort(),
            ].join('|'),
        [futureSymbol, stocks],
    );

    // The effect reads the legs but must not re-run when only their identity
    // changes; `key` above is the real dependency.
    const legs = useRef({ futureSymbol, stocks });
    legs.current = { futureSymbol, stocks };

    useEffect(() => {
        const { futureSymbol: future, stocks: basket } = legs.current;
        if (!enabled || !future || !basket.length) {
            setState({ notional: [], marks: EMPTY_MARKS, loading: false, error: null });
            return;
        }

        let cancelled = false;
        setState((prev) => ({ ...prev, loading: true, error: null }));

        const scale = CANDLE_SCALE[interval];

        (async () => {
            const [futureCandles, ...stockResults] = await Promise.all([
                fetchCandles(future, scale).catch(() => [] as Candle[]),
                ...basket.map((s) => fetchCandles(s.symbol, scale).catch(() => [] as Candle[])),
            ]);
            if (cancelled) return;

            const byStock: Record<string, Candle[]> = {};
            basket.forEach((s, i) => { byStock[s.symbol] = stockResults[i] ?? []; });

            setState({
                notional: buildNotionalSeries(futureCandles, byStock, basket, multiplier),
                // Today by the wall clock, not the last candle: before 09:00 the last
                // candle is yesterday's, and yesterday's close is exactly today's mark.
                marks: previousCloseMarks(futureCandles, byStock, basket, vnDay(Date.now() / 1000)),
                loading: false,
                error: futureCandles.length ? null : 'No candles for the futures leg.',
            });
        })();

        return () => { cancelled = true; };
    }, [key, enabled, multiplier, interval]);

    return state;
}
